import { css } from '@emotion/css';
import {
  Box,
  Card,
  Center,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  Pagination,
  Stack,
  Table,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconCalendar } from '@tabler/icons-react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { BalanceTypes } from 'api/models';
import Invoice from 'app/Private/Invoices/Invoice';
import { SuperButton } from 'components/SuperButton';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  chunk,
  concat,
  first,
  isEmpty,
  isNullish,
  last,
  map,
  pipe,
  prop,
  sort,
  sum,
  unique,
} from 'remeda';
import { useDeletePayment } from 'shared/hooks/use-delete-payment';
import type { DebtorBalance } from 'shared/hooks/use-load-debtor-balance';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';

import { transformToTableRow } from '../balance-utils';

import {
  BalanceSubRows,
  type SortDirection,
  type SortField,
  type TableRow,
  useBalanceTableColumns,
} from './hooks/use-balance-table-columns';

type BalanceTableDetailProps = {
  debtorBalance: DebtorBalance;
  isLoading: boolean;
};

const SHOW_SETTLED_KEY = 'balanceTableShowSettled';

const getInitialShowSettled = () => {
  const stored = localStorage.getItem(SHOW_SETTLED_KEY);
  if (stored === null) return true;
  return stored === 'true';
};

export const BalanceTableDetail = ({ debtorBalance, isLoading }: BalanceTableDetailProps) => {
  const profile = useProfile();
  const { t } = useTranslation();
  const { deletePayment } = useDeletePayment();

  const filters = useForm({
    initialValues: {
      dateRange: [] as Array<Date>,
      types: [] as Array<string>,
      statuses: [] as Array<string>,
      showSettled: getInitialShowSettled(),
    },
  });

  useEffect(() => {
    localStorage.setItem(SHOW_SETTLED_KEY, String(filters.values.showSettled));
  }, [filters.values.showSettled]);

  const [isForceUnfoldActive, { toggle: toggleForceUnfold }] = useDisclosure(false);

  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: 'issueDate' | 'dueDate') => {
    if (sortField === field) setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const [pageIndex, setPageIndex] = useState(1);

  filters.watch('statuses', ({ value: statuses }) => {
    const statusesArray = statuses ?? [];
    if (statusesArray.length > 0) filters.setFieldValue('types', ['invoice']);
  });

  filters.watch('types', ({ value: types, previousValue: previousTypes }) => {
    const typesArray = types ?? [];
    const previousTypesArray = previousTypes ?? [];

    if (previousTypesArray.includes('invoice') && !typesArray.includes('invoice')) {
      filters.setFieldValue('statuses', []);
    }
  });

  const openModal = (id: number, isCreditNote: boolean) => {
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice id={id} isCreditNote={isCreditNote} modal />,
    });
  };

  const statuses = useMemo(() => {
    if (!debtorBalance) return [];

    return pipe(
      debtorBalance.invoices ?? [],
      concat(debtorBalance.creditNotes ?? []),
      map(prop('status')),
      unique(),
      sort((a, b) => a.localeCompare(b)),
    );
  }, [debtorBalance]);

  const allItems = useMemo(() => {
    if (!debtorBalance) return [];
    return transformToTableRow(debtorBalance);
  }, [debtorBalance]);

  const allItemsFilteredByTypeAndDate = useMemo(() => {
    const filteredByStatus = allItems.filter(item => {
      const statusesArray = filters.values.statuses ?? [];
      if (statusesArray.length === 0) return true;
      if (item.type === 'payment') return false;

      return statusesArray.includes(item.status!);
    });

    const filteredByType = filteredByStatus.filter(item => {
      const typesArray = filters.values.types ?? [];
      if (typesArray.length === 0) return true;

      return typesArray.includes(item.type);
    });

    const filteredBySettledAmount = filteredByType.filter(item => {
      if (filters.values.showSettled) return true;

      switch (item.type) {
        case 'invoice':
          return item.balance !== 0;
        case 'credit_note':
          return item.balance !== 0;
        default:
          return true;
      }
    });

    if (isNullish(filters.values.dateRange)) return filteredBySettledAmount;

    const startDate = first(filters.values.dateRange);
    const endDate = last(filters.values.dateRange);

    if (isNullish(startDate) && isNullish(endDate)) return filteredBySettledAmount;

    return filteredBySettledAmount.filter(item => {
      const { issueDate } = item;

      if (startDate && endDate)
        return issueDate.isSameOrAfter(startDate) && issueDate.isSameOrBefore(endDate);

      if (startDate) return issueDate.isSameOrAfter(startDate);

      return issueDate.isSameOrBefore(endDate);
    });
  }, [
    allItems,
    filters.values.statuses,
    filters.values.types,
    filters.values.showSettled,
    filters.values.dateRange,
  ]);

  const types = Object.values(BalanceTypes).map(type => ({
    value: type,
    label:
      type === BalanceTypes.invoice
        ? t(i18nKeys.COMMON.INVOICE)
        : t(tKeysLoose[type.toUpperCase()]),
  }));

  const allItemsSorted = useMemo(() => {
    if (!sortField) return allItemsFilteredByTypeAndDate;

    return [...allItemsFilteredByTypeAndDate].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (!aVal && !bVal) return 0;
      if (!aVal) return sortDirection === 'asc' ? 1 : -1;
      if (!bVal) return sortDirection === 'asc' ? -1 : 1;

      const diff = aVal.valueOf() - bVal.valueOf();
      return sortDirection === 'asc' ? diff : -diff;
    });
  }, [allItemsFilteredByTypeAndDate, sortField, sortDirection]);

  const pages = useMemo(
    () => chunk(allItemsSorted, profile.preferences.itemsPerPage),
    [allItemsSorted, profile.preferences.itemsPerPage],
  );

  const validPageIndex = useMemo(() => {
    if (pages.length === 0) return 1;
    return Math.min(Math.max(pageIndex, 1), pages.length);
  }, [pageIndex, pages.length]);

  useEffect(() => {
    if (validPageIndex !== pageIndex) {
      setPageIndex(validPageIndex);
    }
  }, [validPageIndex, pageIndex]);

  const currentPageData = useMemo(() => {
    if (pages.length === 0) return [];
    const currentPage = pages[validPageIndex - 1];
    return currentPage ?? [];
  }, [pages, validPageIndex]);

  const getAmount = (item: TableRow) => item.balance;

  const tableTotal = pipe(allItemsSorted, map(getAmount), sum());

  const table = useReactTable({
    data: currentPageData,
    columns: useBalanceTableColumns({
      openModal,
      total: tableTotal,
      isForceUnfoldActive,
      toggleForceUnfold,
      sortField,
      sortDirection,
      onSort: handleSort,
    }),
    getRowCanExpand: row => row.original.type !== 'payment',
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  useEffect(() => {
    if (isForceUnfoldActive) table.toggleAllRowsExpanded(true);
    else table.toggleAllRowsExpanded(false);
  }, [isForceUnfoldActive, table]);

  const handleDeletePayment = (paymentId: number, paymentType: string) => {
    deletePayment({ id: paymentId, type: paymentType });
  };

  return (
    <Box>
      <Title my="space6" order={6}>
        {t(i18nKeys.CLIENT.DETAIL.BALANCE_DETAIL_TITLE)}
      </Title>
      <Group mb="space5">
        <DatePickerInput
          clearable
          leftSection={<IconCalendar size={18} stroke={1.5} />}
          type="range"
          placeholder={t(i18nKeys.CLIENT.DETAIL.SELECT_DATES)}
          {...filters.getInputProps('dateRange')}
        />
        <MultiSelect
          miw={210}
          {...filters.getInputProps('statuses')}
          data={statuses}
          comboboxProps={{ shadow: 'md' }}
          placeholder={isEmpty(filters.values.statuses) ? t(i18nKeys.STATUS) : ''}
        />
        <MultiSelect
          miw={210}
          {...filters.getInputProps('types')}
          data={types}
          comboboxProps={{ shadow: 'md' }}
          placeholder={isEmpty(filters.values.types) ? t(i18nKeys.CLIENT.DETAIL.TYPES) : ''}
        />
        <Checkbox
          {...filters.getInputProps('showSettled', { type: 'checkbox' })}
          label={t(i18nKeys.CLIENT.DETAIL.SHOW_SETTLED)}
        />
      </Group>
      <Stack h="75vh">
        <Card radius={8} p={0}>
          <Group style={{ overflowY: 'auto' }}>
            <Table stickyHeader fz={12}>
              <Table.Thead bg="purple.1">
                {table.getHeaderGroups().map(headerGroup => (
                  <Table.Tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Th key={header.id} c="purple.5" style={{ verticalAlign: 'middle' }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </Table.Th>
                    ))}
                    {profile.isSuperAdmin && <Table.Th />}
                  </Table.Tr>
                ))}
              </Table.Thead>
              {table.getRowModel().rows.map(row => (
                <Fragment key={row.id}>
                  <Table.Tbody>
                    <Table.Tr style={{ borderBottom: '1px solid rgb(212, 212, 212)' }}>
                      {row.getVisibleCells().map(cell => (
                        <Table.Td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Td>
                      ))}
                      {profile.isSuperAdmin &&
                        row.original.type === 'payment' &&
                        row.original.paymentId !== undefined && (
                          <Table.Td>
                            <SuperButton
                              onClick={() =>
                                handleDeletePayment(
                                  row.original.paymentId!,
                                  row.original.paymentType!,
                                )
                              }
                            >
                              {t(i18nKeys.DELETE)}
                            </SuperButton>
                          </Table.Td>
                        )}
                    </Table.Tr>
                  </Table.Tbody>
                  {row.getIsExpanded() && (
                    <Table.Tbody className={subRowGroupClass}>
                      <BalanceSubRows row={row} />
                    </Table.Tbody>
                  )}
                </Fragment>
              ))}
              <Table.Tfoot>
                {table.getFooterGroups().map(footerGroup => (
                  <Table.Tr key={footerGroup.id} style={{ borderBottom: 'none' }}>
                    {footerGroup.headers.map(header => (
                      <Table.Td key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.footer, header.getContext())}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tfoot>
            </Table>
          </Group>

          {isLoading && (
            <Center mb="md" w="100%">
              <Loader />
            </Center>
          )}
        </Card>
        <Center>
          <Pagination
            size="sm"
            total={pages.length}
            value={validPageIndex}
            onChange={setPageIndex}
          />
        </Center>
      </Stack>
    </Box>
  );
};

const subRowGroupClass = css`
  tr {
    border-bottom: none;
  }
  tr:last-child {
    border-bottom: 1px solid rgb(212, 212, 212);
  }
`;
